import { useEffect, useState } from "react";
import { Modal, Spin, Descriptions, Tag, Image, Space, Typography } from "antd";
import apiHelpers from "./data";
import { labelForId } from "./stores";
import { triggerNotification } from "../../common/notify";

const ProductDetailModal = ({ open, productId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);

  useEffect(() => {
    if (!open || !productId) {
      setProduct(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiHelpers.fetchProductById(productId);
        if (!cancelled) setProduct(data);
      } catch (err) {
        const mapped = apiHelpers.mapError(err);
        triggerNotification({
          type: "error",
          title: "Failed to load product",
          message: mapped.message,
        });
        if (!cancelled) onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, productId, onClose]);

  const images = apiHelpers.parseImages(product?.prod_img);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={820}
      destroyOnClose
      maskClosable={false}
      title={product?.prod_name || "Product"}
    >
      {loading || !product ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          {images.length > 0 && (
            <Image.PreviewGroup>
              <Space size={[8, 8]} wrap style={{ marginBottom: 16 }}>
                {images.map((src, idx) => (
                  <Image
                    key={`${src}-${idx}`}
                    src={src}
                    width={96}
                    height={96}
                    style={{ objectFit: "cover", borderRadius: 4 }}
                  />
                ))}
              </Space>
            </Image.PreviewGroup>
          )}

          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="SKU">{product.sku || "—"}</Descriptions.Item>
            <Descriptions.Item label="Store">{labelForId(product.store_id)}</Descriptions.Item>
            <Descriptions.Item label="BC Product ID">{product.prod_id || "—"}</Descriptions.Item>
            <Descriptions.Item label="Variant ID">
              {product.variant_id && product.variant_id !== "0" ? product.variant_id : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Type">{product.bc_product_type || "—"}</Descriptions.Item>
            <Descriptions.Item label="Prod type">{product.prod_type || "—"}</Descriptions.Item>
            <Descriptions.Item label="Sale price">
              {product.prod_sale_price ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="MSRP">{product.prod_msrp ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Pricelist">
              {product.pricelist_price ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Inventory">
              {product.inventory_tracking === "none"
                ? "Not tracked"
                : `${product.inventory_level ?? 0} (${product.inventory_tracking})`}
            </Descriptions.Item>
            <Descriptions.Item label="Purchasable">
              {product.is_purchase ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Subscription">
              {product.is_subscription ? <Tag color="blue">Enabled</Tag> : <Tag>No</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="OG Subscription">
              {product.og_subscription_enabled ? <Tag color="blue">Enabled</Tag> : <Tag>No</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Has variants">
              {product.having_variant ? <Tag>Yes</Tag> : <Tag>No</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Sync done">
              {product.sync_done ? <Tag color="green">Yes</Tag> : <Tag color="orange">No</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Updated">
              {product.updated_at
                ? new Date(product.updated_at).toLocaleString("en-GB")
                : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Storefront URL" span={2}>
              {product.bc_url ? (
                <a href={product.bc_url} target="_blank" rel="noopener noreferrer">
                  {product.bc_url}
                </a>
              ) : (
                "—"
              )}
            </Descriptions.Item>
          </Descriptions>

          {product.description && (
            <>
              <Typography.Title level={5} style={{ marginTop: 16 }}>
                Description
              </Typography.Title>
              <div
                style={{
                  border: "1px solid #eee",
                  borderRadius: 4,
                  padding: 12,
                  background: "#fafafa",
                  maxHeight: 300,
                  overflow: "auto",
                }}
              >
                <iframe
                  title="product-description"
                  sandbox=""
                  srcDoc={product.description}
                  style={{ width: "100%", minHeight: 240, border: 0, background: "#fff" }}
                />
              </div>
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default ProductDetailModal;
